import { ArrowRight } from "lucide-react";
import { AppImage as Image } from "@/components/ui/app-image";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { ASPECT_RATIO_CLASS } from "@/lib/aspect-ratios";
import { cn } from "@/lib/utils";
import { HiiveLogoIcon } from "@/svgs/hiive-logo-icon";

export type SeeMoreChipTone = "brand" | "amber" | "emerald" | "sky" | "rose";

/** One fact about what is hidden, tinted by what kind of fact it is. */
export type SeeMoreChip = { label: string; tone?: SeeMoreChipTone };

const CHIP_TONE_CLASS: Record<SeeMoreChipTone, string> = {
  brand: "bg-brand/12 text-brand dark:bg-brand/30 dark:text-violet-100",
  amber:
    "bg-amber-500/15 text-amber-700 dark:bg-amber-400/20 dark:text-amber-200",
  emerald:
    "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-200",
  sky: "bg-sky-500/15 text-sky-700 dark:bg-sky-400/20 dark:text-sky-200",
  rose: "bg-rose-500/15 text-rose-700 dark:bg-rose-400/20 dark:text-rose-200",
};

/** Untoned chips take the next hue in turn, so a row never reads as grey. */
const CHIP_TONE_CYCLE: SeeMoreChipTone[] = ["sky", "emerald", "rose", "amber"];

interface SeeMoreCardProps {
  href: string;
  /** How many items the rail is not showing. Omit when unknown. */
  count?: number | null;
  /** Plural noun for what is hidden: "experiences available", "venues". */
  noun: string;
  /**
   * One short line under the headline saying where the tap goes ("Filter by
   * activity and goals"). For a tile taller than its media plus a headline —
   * the people rail's 520px cards — where the chips alone leave a hole.
   */
  description?: string;
  /**
   * Image URLs from the items this tile stands in for — posters, never video
   * binaries (`mediaPreviewUrlFromAsset`). Up to four are used.
   */
  previewImageUrls?: string[];
  /**
   * Short facts about what is hidden ("12 open today", "Yoga", "7 km away"),
   * as chips under the headline. They wrap to two rows at most and clip
   * after that.
   */
  chips?: SeeMoreChip[];
  /**
   * Chip rows allowed. 1 when `noun` is long enough to wrap the headline:
   * this tile must never be taller than the cards beside it, because the rail
   * stretches every card to its tallest item — a 2-line headline plus 2 chip
   * rows grew every "Find your next class" card by 14px, off screen.
   */
  chipRows?: 1 | 2;
  /** Media frame shape; match the cards beside it. */
  aspectClassName?: string;
  className?: string;
}

/**
 * The last card of a rail that has more than it shows.
 *
 * A capped rail used to just stop, so its end read as the end of what we had.
 * This sits where the next card would be, the same width and height as the
 * cards before it, and shows a glimpse of what is behind it: a collage of the
 * hidden items' own pictures around a liquid-glass disc, and how many there are.
 *
 * The one moving thing is the Hiive bee, on the same `logo-bob` the hiives
 * rail uses. Everything else is still — this is a way on, not an ad.
 */
export function SeeMoreCard({
  href,
  count,
  noun,
  description,
  previewImageUrls = [],
  chips = [],
  chipRows = 2,
  aspectClassName = ASPECT_RATIO_CLASS.portrait4By5,
  className,
}: SeeMoreCardProps) {
  const headline =
    count && count > 0 ? `${count} more ${noun}` : `More ${noun}`;
  const previews = previewImageUrls.slice(0, 4);

  return (
    <BackAwareLink
      href={href}
      aria-label={`See ${headline}`}
      className={cn(
        "group relative flex h-full w-full flex-col overflow-hidden rounded-3xl border border-border bg-surface",
        "transition-[transform,border-color] duration-200 ease-[var(--ease-out-strong)]",
        "hover:border-brand/40 active:scale-[0.98] dark:hover:border-white/20",
        className,
      )}
    >
      <div
        className={cn(
          "relative w-full shrink-0 overflow-hidden bg-surface-alt dark:bg-white/[0.06]",
          aspectClassName,
        )}
      >
        {previews.length > 0 ? (
          <div
            aria-hidden="true"
            className={cn(
              "absolute inset-0 grid gap-0.5",
              previews.length === 1 ? "grid-cols-1" : "grid-cols-2",
              previews.length > 2 && "grid-rows-2",
            )}
          >
            {previews.map((url, index) => (
              <div
                key={url}
                className={cn(
                  "relative overflow-hidden",
                  // Three pictures: the first takes the whole left column.
                  previews.length === 3 && index === 0 && "row-span-2",
                )}
              >
                <Image
                  src={url}
                  alt=""
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 33vw, 15vw"
                />
              </div>
            ))}
          </div>
        ) : (
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-br from-brand/35 via-surface-alt to-[#00c2ff]/20 dark:from-brand/40 dark:via-[#12101c] dark:to-[#00c2ff]/15"
          />
        )}

        {/* The pictures stay legible — they are the point. Only a soft vignette
            sits over them, and the glass is kept to the disc in the middle. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.05)_0%,rgba(0,0,0,0.35)_100%)]"
        />

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex size-20 items-center justify-center rounded-full border border-white/50 bg-white/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_10px_28px_-10px_rgba(0,0,0,0.55)] backdrop-blur-xl backdrop-saturate-150 dark:border-white/20 dark:bg-white/10">
            <div className="animate-logo-bob motion-reduce:animate-none">
              <HiiveLogoIcon
                width={44}
                height={44}
                className="drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col bg-surface/90 px-3 pt-3 pb-3 text-text-primary dark:bg-surface/80 dark:text-white">
        <span className="line-clamp-2 text-base leading-tight font-semibold tabular-nums">
          {headline}
        </span>
        {description ? (
          <span className="mt-1 line-clamp-2 text-[13px] leading-snug font-medium text-text-tertiary dark:text-white/60">
            {description}
          </span>
        ) : null}
        {chips.length > 0 ? (
          // Two rows and never a third: this tile is stretched to the height
          // of the cards beside it, so anything taller here makes every card
          // in the rail taller with it. 2 × h-6 + the gap is the cap.
          <span
            className={cn(
              "mt-2 flex min-w-0 flex-wrap gap-1 overflow-hidden",
              chipRows === 1 ? "max-h-6" : "max-h-[52px]",
            )}
          >
            {chips.map((chip, index) => (
              <span
                key={chip.label}
                className={cn(
                  "inline-flex h-6 shrink-0 items-center rounded-full px-2 text-[11px] leading-none font-semibold",
                  CHIP_TONE_CLASS[
                    chip.tone ??
                      CHIP_TONE_CYCLE[index % CHIP_TONE_CYCLE.length] ??
                      "sky"
                  ],
                )}
              >
                {chip.label}
              </span>
            ))}
          </span>
        ) : description ? null : (
          <span className="mt-1 text-[13px] font-medium text-text-tertiary dark:text-white/60">
            Keep exploring
          </span>
        )}
        <span className="mt-auto pt-3" />
        <span className="flex h-10 w-full items-center justify-center gap-1.5 rounded-xl border border-brand/30 bg-brand/[0.06] pt-0 text-[13px] font-semibold text-brand dark:border-brand/45 dark:bg-brand/[0.14] dark:text-violet-200">
          See all
          <ArrowRight
            aria-hidden="true"
            className="size-4 transition-transform duration-200 ease-[var(--ease-out-strong)] group-hover:translate-x-0.5"
          />
        </span>
      </div>
    </BackAwareLink>
  );
}
